import PropTypes from 'prop-types'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import constants from 'app/constants'
import fetchJSONData from 'app/util/fetchJSONData'
import Content from 'components/content/Content/Content'
import ScrollRegion from 'components/core/ScrollRegion/ScrollRegion'
import ButtonTab from 'components/elements/ButtonTab/ButtonTab'
import Spinner from 'components/elements/Spinner/Spinner'
import classNames from 'lib/util/classNames'
import styles from './PrivacyTermsDialog.module.scss'

let cache = null

/**
 * The `PrivacyTermsDialog`
 *
 * @param {object} props - the component props
 * @returns {React.ReactElement} the element
 */
function PrivacyTermsDialog (props) {
  const { className } = props
  const [data, setData] = useState(cache)
  const [error, setError] = useState(null)
  const [currentTab, setCurrentTab] = useState('privacy')
  const isMounted = useRef(false)
  const classNameOutput = useMemo(
    () => classNames([className, styles.container]),
    [className]
  )
  const bodyRef = useRef()

  const loadData = useCallback(async () => {
    const { data, success } = await fetchJSONData(
      `${constants.PATH_DATA}/privacy_terms.json?ver=${constants.CACHE_BUST_VERSION}`
    )

    if (!isMounted.current) {
      // No longer mounted
      return
    }

    if (!success) {
      setError('There was a problem loading the data. Please try again later.')

      return
    }

    const res = data

    cache = res

    setData(cache)
  }, [])

  const output = useMemo(() => {
    if (!data && !error) {
      return <Spinner />
    }

    if (error) {
      return (
        <div className={styles.error}>
          <p>{error}</p>
        </div>
      )
    }

    if (currentTab === 'privacy') {
      return <Content items={data.privacy} />
    }

    return <Content items={data.terms} />
  }, [data, error, currentTab])

  /**
   * Effect: When the `currentTab` changes, reset the scroll
   */
  useEffect(() => {
    bodyRef.current.scrollTo(0, 0)
  }, [currentTab])

  /**
   * Effect: On mount
   */
  useEffect(() => {
    isMounted.current = true

    if (!cache) {
      loadData()
    }

    return () => {
      isMounted.current = false
    }
  }, [])

  return (
    <div className={classNameOutput}>
      <div className={styles.tabs}>
        <ButtonTab
          className={styles.tab}
          isSelected={currentTab === 'privacy'}
          color="b"
          size="medium"
          isActive={false}
          isTransitionActive={true}
          index={1}
          onClick={() => setCurrentTab('privacy')}
        >
          Privacy
        </ButtonTab>
        <ButtonTab
          className={styles.tab}
          isSelected={currentTab === 'terms'}
          size="medium"
          color="b"
          isActive={false}
          isTransitionActive={true}
          index={2}
          onClick={() => setCurrentTab('terms')}
        >
          Terms
        </ButtonTab>
      </div>
      <ScrollRegion className={styles.body} ref={bodyRef}>
        {output}
      </ScrollRegion>
    </div>
  )
}

/** @type {object} */
PrivacyTermsDialog.propTypes = {
  className: PropTypes.string
}

/** @type {object} */
PrivacyTermsDialog.defaultProps = {
  className: ''
}

// Memoize
export default memo(PrivacyTermsDialog)
